import { Delopay } from './client';
import type { DelopayLogger, RequestExtras } from './client';
import { DelopayError } from './error';
import type {
  CheckoutDataResponse,
  ConfirmSubscriptionRequest,
  ConfirmSubscriptionResponse,
  EpayoutsMethodsOptions,
  EpayoutsMethodsResponse,
  EpayoutsPaypalCaptureRequest,
  EpayoutsPaypalCaptureResponse,
  EpayoutsPaypalOrderResponse,
  PaymentAbandonAttemptResponse,
  PaymentConfirmRequest,
  PaymentMethodListResponse,
  PaymentResponse,
  PaymentsPostSessionTokensRequest,
  PaymentsPostSessionTokensResponse,
  PaymentUpdateRequest,
  RecordCheckoutEventRequest,
  RecordCheckoutEventResponse,
  ReplayIngestRequest,
  ReplayIngestResponse,
  NomupayCardAuthorizationRequest,
  NomupayCardAuthorizationResponse,
  StripeHostedConfirmRequest,
  StripeHostedConfirmResponse,
  VaultCollectSessionResponse,
  VaultPaymentMethodRequest,
  VaultPaymentMethodResponse,
} from './types';

/**
 * A publishable (browser-safe) API key. The template type rejects secret
 * keys (`prd_…` / `snd_…`) at compile time, so a checkout cannot be handed
 * a credential that would reach secret-key routes.
 */
export type PublishableKey = `pk_${string}`;

/**
 * Drop any caller-supplied credential headers (case-insensitively), so a
 * route can only ever carry the one credential its family requires — the
 * class's isolation guarantee must hold against per-call `headers` too.
 * Non-credential extras (`Idempotency-Key`, `X-Dp-*`, …) pass through.
 */
function withoutCredentialHeaders(extra?: Record<string, string>): Record<string, string> {
  if (!extra) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(extra)) {
    const lower = key.toLowerCase();
    if (lower === 'api-key' || lower === 'authorization') continue;
    out[key] = value;
  }
  return out;
}

/** Drop one header (case-insensitively) from a caller-supplied extras map. */
function withoutHeader(headers: Record<string, string>, name: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === name) continue;
    out[key] = value;
  }
  return out;
}

/** Configuration for a {@link CheckoutSession}. */
export interface CheckoutSessionOptions {
  /** The merchant the payment belongs to. */
  merchantId: string;
  /** The payment this session is about. */
  paymentId: string;
  /**
   * The merchant's publishable key (`pk_prd_…` / `pk_snd_…`), from the
   * checkout payload's `pub_key`. Required for the `/payments/*` and
   * `/payment-methods` calls.
   */
  publishableKey?: PublishableKey;
  /**
   * The payment's client secret, from the checkout payload. Required for
   * every `/payment-link/*` call, and rides along on the payment calls.
   */
  clientSecret?: string;
  /** Override the API base URL (e.g. `/api` behind a same-origin proxy). */
  baseUrl?: string;
  /** Use the sandbox environment. Ignored when `baseUrl` is set. */
  sandbox?: boolean;
  /** Per-request timeout in milliseconds. */
  timeout?: number;
  /** Maximum automatic retries for retryable requests. */
  maxRetries?: number;
  debug?: boolean;
  logger?: DelopayLogger;
}

/**
 * Buyer-side client for a single hosted-checkout payment.
 *
 * Binds the two browser-safe credentials once — the merchant's publishable
 * key and the payment's client secret — and sends each request with exactly
 * the credential its route expects:
 *
 * - `/payment-link/*` side-channel routes authenticate with
 *   `Authorization: Bearer <client_secret>`.
 * - `/payments/*` and `/payment-methods` authenticate with the publishable
 *   key in the `api-key` header, with the client secret as query parameter
 *   or body field.
 *
 * Neither credential can reach a secret-key route: the publishable key is
 * typed to the `pk_` prefix and the client secret only ever leaves as a
 * bearer token / parameter, never as an `api-key`.
 *
 * @example
 * ```typescript
 * const session = new CheckoutSession({
 *   merchantId: checkout.merchant_id,
 *   paymentId: checkout.payment_id,
 *   publishableKey: checkout.pub_key,
 *   clientSecret: checkout.client_secret,
 * });
 * const catalog = await session.epayoutsMethods('de');
 * ```
 */
/**
 * A `CompressionStream` constructor, as much of it as this module uses.
 *
 * Declared structurally rather than taken from the DOM lib: the global is
 * absent in some runtimes this SDK is dropped into, and a type that asserts it
 * exists would make the feature test look redundant to the next reader.
 */
type GzipStreamCtor = new (format: 'gzip') => {
  readable: ReadableStream<Uint8Array>;
  writable: WritableStream<Uint8Array>;
};

/**
 * Gzip a JSON body, or answer `null` where the runtime cannot.
 *
 * `null` is not a failure — it is the caller's instruction to send the body
 * uncompressed. `CompressionStream` is a global in Node 18+ and in current
 * browsers but is missing from older Safari and from some embedded runtimes,
 * and this SDK carries no runtime dependencies, so there is no polyfill to
 * reach for. Sending plain JSON is always correct; the server reads the
 * encoding off the header it is given.
 *
 * The compressed stream is drained to an `ArrayBuffer` rather than handed to
 * `fetch` as a stream. A streamed request body needs `duplex: 'half'`, cannot
 * be replayed on a retry, and is refused outright together with `keepalive` —
 * which is precisely the case a recorder needs when the page is navigating
 * away.
 */
async function gzipJson(value: unknown): Promise<ArrayBuffer | null> {
  const ctor = (globalThis as { CompressionStream?: GzipStreamCtor }).CompressionStream;
  if (typeof ctor !== 'function') return null;
  try {
    const bytes = new TextEncoder().encode(JSON.stringify(value));
    return await new Response(
      new Blob([bytes]).stream().pipeThrough(new ctor('gzip')),
    ).arrayBuffer();
  } catch {
    // A runtime carrying the constructor but unable to run it — no
    // `Blob.prototype.stream`, no `pipeThrough` — is the uncompressed case
    // too. A recording must never cost the caller a payment page.
    return null;
  }
}

export class CheckoutSession {
  private readonly client: Delopay;
  private readonly merchantId: string;
  private readonly paymentId: string;
  private readonly publishableKey?: PublishableKey;
  private readonly clientSecret?: string;

  constructor(options: CheckoutSessionOptions) {
    this.merchantId = options.merchantId;
    this.paymentId = options.paymentId;
    this.publishableKey = options.publishableKey;
    this.clientSecret = options.clientSecret;
    // Constructed WITHOUT a key: every call attaches its own credential
    // explicitly, so a bearer-authenticated route never carries an api-key
    // header and vice versa.
    this.client = new Delopay('', {
      baseUrl: options.baseUrl,
      sandbox: options.sandbox,
      timeout: options.timeout,
      maxRetries: options.maxRetries,
      debug: options.debug,
      logger: options.logger,
    });
  }

  private get linkBase(): string {
    return `/payment-link/${encodeURIComponent(this.merchantId)}/${encodeURIComponent(this.paymentId)}`;
  }

  /** Headers for the client-secret bearer routes (`/payment-link/*`). */
  private bearerHeaders(extra?: Record<string, string>): Record<string, string> {
    return {
      ...withoutCredentialHeaders(extra),
      Authorization: `Bearer ${this.requireClientSecret()}`,
    };
  }

  /** Headers for the publishable-key routes (`/payments/*`, `/payment-methods`). */
  private pkHeaders(extra?: Record<string, string>): Record<string, string> {
    if (!this.publishableKey) {
      throw new DelopayError('This call requires the publishable key', {
        status: 0,
        code: 'MISSING_CREDENTIAL',
        type: 'invalid_request',
      });
    }
    return { ...withoutCredentialHeaders(extra), 'api-key': this.publishableKey };
  }

  private requireClientSecret(): string {
    if (!this.clientSecret) {
      throw new DelopayError('This call requires the payment client secret', {
        status: 0,
        code: 'MISSING_CREDENTIAL',
        type: 'invalid_request',
      });
    }
    return this.clientSecret;
  }

  /**
   * The hosted checkout's bootstrap payload for a one-time payment —
   * `CheckoutDetails` while payable, a status view once settled.
   *
   * Deliberately unauthenticated: a `pay_` link bootstraps before any
   * credential exists, so no credential header is ever attached (caller
   * extras are still stripped of credentials). Core fields and the collection
   * policy are typed; provider-specific fields remain available as unknown.
   *
   * `theme` (a named checkout variant) and `country` are the route's declared
   * query parameters. `country` is a manual override of the buyer country the
   * router otherwise resolves from the client IP — it decides which payment
   * methods the layout shows — as an ISO 3166-1 alpha-2 code, sent uppercased
   * and omitted when empty. `locale` travels as `Accept-Language` — the only
   * channel the backend's locale resolution reads; a `?locale=` query is
   * silently ignored by this route.
   *
   * `GET /payment-link/data/{merchantId}/{paymentId}`
   */
  async fetchCheckoutData(
    params?: { locale?: string; theme?: string; country?: string },
    options?: RequestExtras,
  ): Promise<CheckoutDataResponse> {
    const country = params?.country?.trim().toUpperCase();
    return this.client.request(
      'GET',
      `/payment-link/data/${encodeURIComponent(this.merchantId)}/${encodeURIComponent(this.paymentId)}`,
      {
        query: { theme: params?.theme, country: country || undefined },
        ...options,
        headers: {
          ...(params?.locale
            ? {
                ...withoutHeader(withoutCredentialHeaders(options?.headers), 'accept-language'),
                'Accept-Language': params.locale,
              }
            : withoutCredentialHeaders(options?.headers)),
        },
      },
    );
  }

  /**
   * The subscription twin of {@link CheckoutSession.fetchCheckoutData}: the
   * bootstrap payload for a `sub_` checkout. Authenticated with the
   * subscription's client secret (construct the session with the `sub_…` id
   * in the `paymentId` slot). `locale` travels as `Accept-Language`, same
   * as {@link CheckoutSession.fetchCheckoutData}.
   *
   * `GET /subscriptions/data/{merchantId}/{subscriptionId}`
   */
  async fetchSubscriptionData(
    params?: { locale?: string },
    options?: RequestExtras,
  ): Promise<Record<string, unknown>> {
    return this.client.request(
      'GET',
      `/subscriptions/data/${encodeURIComponent(this.merchantId)}/${encodeURIComponent(this.paymentId)}`,
      {
        ...options,
        headers: {
          ...(params?.locale
            ? {
                ...withoutHeader(this.bearerHeaders(options?.headers), 'accept-language'),
                'Accept-Language': params.locale,
              }
            : this.bearerHeaders(options?.headers)),
        },
      },
    );
  }

  /**
   * Report buyer/device signals for rails that never reach
   * `/payments/{id}/confirm` (the Stripe SAQ-A rail: raw cards, Apple Pay,
   * Google Pay). Same telemetry contract as
   * {@link CheckoutSession.recordEvent}: sent with `keepalive: true`, and
   * failures resolve instead of rejecting — signals must never block or
   * break a checkout. Unauthenticated by design.
   *
   * `POST /payment-link/client-signals/{merchantId}/{paymentId}`
   */
  async reportClientSignals(
    params: {
      browser_info?: Record<string, unknown>;
      signals: Record<string, unknown>;
    },
    options?: RequestExtras,
  ): Promise<void> {
    try {
      await this.client.request(
        'POST',
        `/payment-link/client-signals/${encodeURIComponent(this.merchantId)}/${encodeURIComponent(this.paymentId)}`,
        {
          body: params,
          keepalive: true,
          ...options,
          headers: withoutCredentialHeaders(options?.headers),
        },
      );
    } catch {
      // Telemetry: swallowing is the contract, not an oversight.
    }
  }

  /**
   * Store one slice of the buyer's checkout recording on our own side.
   * Called by the hosted checkout as the buyer works through the page; the
   * client secret is attached automatically.
   *
   * **Not telemetry semantics, on purpose.** Unlike
   * {@link CheckoutSession.recordEvent}, this rejects with a `DelopayError`
   * like every other payment call, because the recorder has to tell the
   * answers apart: a `400` with code `IR_16` means the backend will not take
   * this recording (recording is off on that deployment, the recording is
   * full, the payment is already recording a different session, or this
   * number is stored with different content) and the recorder must stop; any
   * other `400` is the slice itself being refused; a transport failure may be
   * retried with the **same** bytes under the **same** number, which the
   * backend answers with `duplicate: true`.
   *
   * Pass `keepalive: true` for the last slice of a page that is navigating
   * away (a redirect rail); browsers refuse keepalive bodies over ~64 KiB, so
   * only a small final slice should carry it.
   *
   * Pass `compress: true` to send the slice gzipped. Recorded page events
   * compress heavily, so this is mostly a saving on the buyer's connection. It
   * is opt-in and degrades silently: a runtime without `CompressionStream`
   * sends the same slice uncompressed rather than failing, so a caller never
   * has to feature-test before asking for it.
   *
   * ⚠️ `keepalive` overrides it. Compressing means awaiting a stream read
   * before the request is made, and `keepalive` protects a request only once
   * it has been initiated — so a page torn down mid-compression would drop the
   * final slice altogether. A slice sent with `keepalive` therefore always
   * travels uncompressed, whatever `compress` says. Passing both is allowed and
   * is what a recorder should do: every ordinary slice compresses, and the last
   * one is sent the way that actually arrives.
   *
   * `POST /payments/{paymentId}/replay/slice` (publishable key)
   */
  async storeReplaySlice(
    params: Omit<ReplayIngestRequest, 'client_secret'>,
    options?: RequestExtras & { keepalive?: boolean; compress?: boolean },
  ): Promise<ReplayIngestResponse> {
    const { keepalive, compress, ...extras } = options ?? {};
    // Built before anything can be compressed, so a missing credential still
    // rejects without a request going out.
    const body = { ...params, client_secret: this.requireClientSecret() };
    const headers = this.pkHeaders(extras.headers);
    // `keepalive` wins over `compress`, and that is a correctness rule rather
    // than a preference. `keepalive` preserves a request only once it has been
    // *initiated*; compressing first means awaiting a stream read, and a page
    // torn down during that await never reaches `fetch()` at all — the final
    // slice is then lost outright, which is the one thing `keepalive` exists to
    // prevent. Losing a slice to save bytes is the wrong trade, so the slice a
    // page is navigating away from travels uncompressed. It is also the
    // smallest one: browsers refuse keepalive bodies over ~64 KiB anyway.
    const gzipped = compress === true && keepalive !== true ? await gzipJson(body) : null;
    return this.client.request(
      'POST',
      `/payments/${encodeURIComponent(this.paymentId)}/replay/slice`,
      {
        body: gzipped ?? body,
        ...(keepalive !== undefined ? { keepalive } : {}),
        ...extras,
        // `Content-Type` has to be stated for the compressed body: the client
        // only sets it for a body it serialises itself, and the bytes are
        // still JSON — gzip is how they travel, not what they are.
        headers:
          gzipped === null
            ? headers
            : { ...headers, 'Content-Type': 'application/json', 'Content-Encoding': 'gzip' },
      },
    );
  }

  /**
   * Confirm a subscription (PayPal approval rail). The client secret is
   * attached automatically; the shop's profile id travels as the
   * `X-Profile-Id` header the subscription routes require.
   *
   * `POST /subscriptions/{subscriptionId}/confirm` (publishable key)
   */
  async confirmSubscription(
    subscriptionId: string,
    profileId: string,
    params: Omit<ConfirmSubscriptionRequest, 'client_secret'>,
    options?: RequestExtras,
  ): Promise<ConfirmSubscriptionResponse> {
    return this.client.request(
      'POST',
      `/subscriptions/${encodeURIComponent(subscriptionId)}/confirm`,
      {
        body: { ...params, client_secret: this.requireClientSecret() },
        ...options,
        // Strip any caller-supplied x-profile-id first (case-insensitively):
        // Fetch folds duplicate headers into `caller, pro_x`, and the
        // subscription routes must see exactly one profile scope.
        headers: {
          ...withoutHeader(this.pkHeaders(options?.headers), 'x-profile-id'),
          'X-Profile-Id': profileId,
        },
      },
    );
  }

  /**
   * The e-Payouts rail catalog for the buyer's country, plus the set of
   * countries that have at least one vendor.
   *
   * `GET /payment-link/{merchantId}/{paymentId}/epayouts/methods`
   *
   * Pass `{ paypalSdk: true }` from a checkout drawing an experimental PayPal
   * SDK pane to also receive e-Payouts' PayPal client id
   * (`paypal_client_id`, `paypal_currency`); it is present only when `ppal`
   * is among the methods.
   *
   * @param country - Lowercase ISO 3166-1 alpha-2 country code.
   */
  async epayoutsMethods(
    country: string,
    options?: RequestExtras & EpayoutsMethodsOptions,
  ): Promise<EpayoutsMethodsResponse> {
    const { paypalSdk, merchantConnectorId, ...extras } = options ?? {};
    return this.client.request('GET', `${this.linkBase}/epayouts/methods`, {
      query: paypalSdk
        ? {
            cc: country,
            paypal_sdk: 'true',
            ...(merchantConnectorId ? { merchant_connector_id: merchantConnectorId } : {}),
          }
        : { cc: country },
      ...extras,
      headers: this.bearerHeaders(extras.headers),
    });
  }

  /**
   * The PayPal order for the payment's e-Payouts access code, from the PayPal
   * SDK's `createOrder` on an experimental PayPal SDK pane — after the payment
   * was confirmed through that pane, which mints the code.
   *
   * The router reads the order off e-Payouts' own PayPal page for the code, so
   * `amount` and `currency` are what e-Payouts charges for it. Repeated calls
   * answer the same order, so a cancelled PayPal popup is retried with the same
   * code. When `captured` is `true` a capture was already reported: create no
   * order and wait for the payment's status instead.
   *
   * `POST /payment-link/{merchantId}/{paymentId}/epayouts/paypal-order`
   */
  async epayoutsPaypalOrder(options?: RequestExtras): Promise<EpayoutsPaypalOrderResponse> {
    return this.client.request('POST', `${this.linkBase}/epayouts/paypal-order`, {
      ...options,
      headers: this.bearerHeaders(options?.headers),
    });
  }

  /**
   * Report a PayPal order's progress from `onApprove` on an experimental PayPal
   * SDK pane: the approval, before asking PayPal to capture — and capture only
   * once the router has it — then the capture PayPal answered, or a decline.
   * When the approval's answer is lost, do not capture: report `NOT_CAPTURED`
   * instead, which releases an approval the router may have recorded.
   *
   * The router records each on the payment and relays a capture that took money
   * to e-Payouts the way e-Payouts' own PayPal page does. It does not mark the
   * payment paid: that happens only on e-Payouts' notification, so poll the
   * payment's status afterwards. A repeated report is answered, not recorded
   * twice.
   *
   * `POST /payment-link/{merchantId}/{paymentId}/epayouts/paypal-capture`
   */
  async epayoutsPaypalCapture(
    params: EpayoutsPaypalCaptureRequest,
    options?: RequestExtras,
  ): Promise<EpayoutsPaypalCaptureResponse> {
    return this.client.request('POST', `${this.linkBase}/epayouts/paypal-capture`, {
      body: params,
      ...options,
      headers: this.bearerHeaders(options?.headers),
    });
  }

  /**
   * Record a buyer-side checkout event on the payment's status timeline.
   *
   * Telemetry semantics, built in so callers can genuinely fire-and-forget:
   * the request is sent with `keepalive: true` (it survives the document
   * navigating away, e.g. right before a `window.open`), and transport or
   * server failures resolve to `undefined` instead of rejecting — telemetry
   * must never break a checkout or surface an unhandled rejection. Do not
   * `await` this in a click handler that must stay synchronous.
   *
   * A missing client secret still throws `MISSING_CREDENTIAL`: that is a
   * wiring bug, not a telemetry failure.
   *
   * `POST /payment-link/{merchantId}/{paymentId}/checkout-events`
   */
  async recordEvent(
    params: RecordCheckoutEventRequest,
    options?: RequestExtras,
  ): Promise<RecordCheckoutEventResponse | undefined> {
    const headers = this.bearerHeaders(options?.headers);
    try {
      return await this.client.request('POST', `${this.linkBase}/checkout-events`, {
        body: params,
        keepalive: true,
        ...options,
        headers,
      });
    } catch {
      return undefined;
    }
  }

  /**
   * A short-lived VGS Collect session for browser-side card capture.
   *
   * **Exactly one refusal means "this shop has no vault": a 400 carrying
   * `IR_19`.** Every other refusal means a vault exists and could not be used,
   * and answering it by falling back to the processor's own card pane sends an
   * unprotected card number to the very processor the shop pays to hide it
   * from — the bug this endpoint's error contract exists to prevent.
   *
   * A **404 is not benign.** The router answers it when the shop's vault
   * account cannot be found — the state a shop is left in when its vault
   * connector is deleted while the profile keeps naming the id: it still
   * reports the vault as enabled and still expects its cards cloaked. The same
   * status also covers a payment that does not exist.
   *
   * `GET /payment-link/{merchantId}/{paymentId}/vault/collect-session`
   */
  async vaultCollectSession(options?: RequestExtras): Promise<VaultCollectSessionResponse> {
    return this.client.request('GET', `${this.linkBase}/vault/collect-session`, {
      ...options,
      headers: this.bearerHeaders(options?.headers),
    });
  }

  /**
   * Register the aliased card as a payment method and mint the one-shot
   * `payment_token` the confirm call spends.
   *
   * `POST /payment-link/{merchantId}/{paymentId}/vault/payment-method`
   */
  async registerVaultPaymentMethod(
    params: VaultPaymentMethodRequest,
    options?: RequestExtras,
  ): Promise<VaultPaymentMethodResponse> {
    return this.client.request('POST', `${this.linkBase}/vault/payment-method`, {
      body: params,
      ...options,
      headers: this.bearerHeaders(options?.headers),
    });
  }

  /**
   * Confirm the Stripe card form's PaymentIntent through the router, for a
   * checkout payload with `card_connector_server_confirm: true`. Stripe refuses
   * a Stripe.js confirm of such an intent.
   *
   * Send the PaymentMethod `stripe.createPaymentMethod` produced from the
   * Payment Element (or Express Checkout Element), which needs
   * `paymentMethodCreation: 'manual'` on the Elements instance. A
   * ConfirmationToken is refused with `400`: Stripe will not confirm such an
   * intent with one.
   *
   * The router judges the card's issuing country and the buyer's IP country
   * against the shop's sell-to restrictions before Stripe authorises, and a
   * refusal throws a `DelopayError` with code `DE_10`. When the answer's
   * `status` is `requires_action`, run `stripe.handleNextAction` with its
   * `client_secret` — safe, because the intent is `confirmation_method=manual`
   * and that secret confirms nothing without the secret key — then call this
   * again without a payment method to finalize.
   *
   * A PaymentMethod rules out what `stripe.createPaymentMethod` cannot
   * produce: Stripe names Amazon Pay and Klarna in the Express Checkout
   * Element, which need `stripe.createConfirmationToken` instead. Neither can
   * be paid through this route, so a caller must not offer them on a
   * server-confirmed intent — mount the Express Checkout Element with those
   * methods `'never'`, as DeloPay's own checkout does. The router drops only
   * the methods its server-side confirm cannot finish at all (BLIK and ACSS
   * pre-authorized debits) from the card form's mount, so the rest of the
   * selection still reaches the element and is the caller's to constrain.
   *
   * `POST /payment-link/{merchantId}/{paymentId}/stripe-confirm`
   */
  async confirmStripeCardForm(
    params: StripeHostedConfirmRequest,
    options?: RequestExtras,
  ): Promise<StripeHostedConfirmResponse> {
    return this.client.request('POST', `${this.linkBase}/stripe-confirm`, {
      body: params,
      ...options,
      headers: this.bearerHeaders(options?.headers),
    });
  }

  /**
   * Ask whether NomuPay's COPYandPAY widget may submit a card, from the
   * form's pre-submit hook.
   *
   * Only for a checkout stamped `NomupayOppwaCheckoutData.submit_grant`: the
   * card form sits beside always-shown wallet buttons of another account, and
   * this answer closes those buttons before it allows the card. `authorized`
   * is `false` when the payment no longer waits for a payment method, or when
   * this is not the attempt's live card-form checkout; when a wallet beside it
   * is already being paid, the route answers an error instead. Submit the
   * widget only on `true`.
   *
   * `POST /payment-link/{merchantId}/{paymentId}/nomupay-card-authorization`
   */
  async nomupayCardAuthorization(
    params: NomupayCardAuthorizationRequest,
    options?: RequestExtras,
  ): Promise<NomupayCardAuthorizationResponse> {
    return this.client.request('POST', `${this.linkBase}/nomupay-card-authorization`, {
      body: params,
      ...options,
      headers: this.bearerHeaders(options?.headers),
    });
  }

  /**
   * The payment's current state — status polling for redirect/popup rails.
   *
   * `GET /payments/{paymentId}` (publishable key + client secret)
   */
  async retrievePayment(options?: RequestExtras): Promise<PaymentResponse> {
    return this.client.request('GET', `/payments/${encodeURIComponent(this.paymentId)}`, {
      query: { client_secret: this.requireClientSecret() },
      ...options,
      headers: this.pkHeaders(options?.headers),
    });
  }

  /**
   * Update the payment before confirmation (e.g. persist custom-field
   * answers as `metadata` on rails that never hit `/confirm`). The client
   * secret is attached automatically.
   *
   * `POST /payments/{paymentId}` (publishable key)
   */
  async updatePayment(
    params: PaymentUpdateRequest,
    options?: RequestExtras,
  ): Promise<PaymentResponse> {
    return this.client.request('POST', `/payments/${encodeURIComponent(this.paymentId)}`, {
      body: { ...params, client_secret: this.requireClientSecret() },
      ...options,
      headers: this.pkHeaders(options?.headers),
    });
  }

  /**
   * Confirm the payment. The client secret is attached automatically; pass
   * an `Idempotency-Key` header via `options` to make retries safe.
   *
   * `POST /payments/{paymentId}/confirm` (publishable key)
   */
  async confirmPayment(
    params: PaymentConfirmRequest,
    options?: RequestExtras,
  ): Promise<PaymentResponse> {
    return this.client.request('POST', `/payments/${encodeURIComponent(this.paymentId)}/confirm`, {
      body: { ...params, client_secret: this.requireClientSecret() },
      ...options,
      headers: this.pkHeaders(options?.headers),
    });
  }

  /**
   * Mint the processor-side session a browser SDK completes — the PayPal
   * order that PayPal's buttons or Card Fields attach the buyer's choice to.
   * The client secret is attached automatically.
   *
   * For PayPal Card Fields pass `payment_method: 'card'` and the
   * `merchant_connector_id` the checkout was rendered with; the order id comes
   * back in `next_action.next_action_data.order_id` and is what the SDK's
   * `createOrder` returns. The confirm that captures it sends the same order
   * and account as `payment_method_data.card_reference`.
   *
   * Send a distinct `Idempotency-Key` through `options.headers` for each new
   * card submission; reuse it only to retry that same session request. The
   * returned order is bound to this attempt and account. Sessions cannot
   * replace an order while authentication or money processing is unresolved,
   * or after authorization/capture; stale or foreign references are refused.
   *
   * `POST /payments/{paymentId}/post-session-tokens` (publishable key)
   */
  async postSessionTokens(
    params: PaymentsPostSessionTokensRequest,
    options?: RequestExtras,
  ): Promise<PaymentsPostSessionTokensResponse> {
    return this.client.request(
      'POST',
      `/payments/${encodeURIComponent(this.paymentId)}/post-session-tokens`,
      {
        body: { ...params, client_secret: this.requireClientSecret() },
        ...options,
        headers: this.pkHeaders(options?.headers),
      },
    );
  }

  /**
   * Abandon the attempt the buyer left open on a connector's hosted page
   * (the payment sits in `requires_customer_action`) and open a fresh attempt
   * awaiting a payment method, so the same payment can be confirmed again
   * with another method or connector. Nothing has been authorised on the
   * abandoned attempt; it is voided locally and kept in the attempt history.
   * Idempotent: a payment already in `requires_payment_method` comes back
   * unchanged with `abandoned_attempt_id: null`.
   *
   * `POST /payments/{paymentId}/abandon-attempt` (publishable key + client secret)
   */
  async abandonAttempt(options?: RequestExtras): Promise<PaymentAbandonAttemptResponse> {
    return this.client.request(
      'POST',
      `/payments/${encodeURIComponent(this.paymentId)}/abandon-attempt`,
      {
        body: { client_secret: this.requireClientSecret() },
        ...options,
        headers: this.pkHeaders(options?.headers),
      },
    );
  }

  /**
   * Payment methods available for this payment.
   *
   * `GET /payment-methods` (publishable key + client secret)
   *
   * @param params - Optional filters; `country` is the highest-precedence
   *   geo hint, ahead of billing address and IP geolocation.
   */
  async listPaymentMethods(
    params?: { country?: string },
    options?: RequestExtras,
  ): Promise<PaymentMethodListResponse> {
    return this.client.request('GET', '/payment-methods', {
      query: { client_secret: this.requireClientSecret(), country: params?.country },
      ...options,
      headers: this.pkHeaders(options?.headers),
    });
  }
}
